import React, { useEffect, useMemo, useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import DataKeys from 'k8s/DataKeys'
import { ArrayElement } from 'core/actions/Action'
import { clusterAddonsSelector } from '../selectors'
import { listClusterAddons } from '../new-actions'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import ListContainer from 'core/containers/ListContainer'
import useListAction from 'core/hooks/useListAction'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import { pick } from 'ramda'
import {
  clusterAddonDisplayNames,
  getClusterAddonHealthStatus,
  createEtcdBackupAddonObj,
  isUninstalling,
} from '../helpers'
import BareMetalCard from './BareMetalCard'
import { BadgeVariant } from 'core/elements/badge/Badge'
import AddonHealthStatusesCard from './AddonHealthStatusesCard'
import Button from 'core/elements/button'
import useReactRouter from 'use-react-router'
import { routes } from 'core/utils/routes'
import { ClusterTypes, IClusterEditPageTabs } from '../../model'
import { IEditImportedClusterPageTabs } from 'app/plugins/infrastructure/components/importedClusters/model'
import { createGridStatusCell } from 'core/elements/grid/cells/GridStatusCell'
import InferActionParams from 'core/actions/InferActionParams'
import { ClusterAddonType } from '../model'

type ModelDataKey = DataKeys.ClusterAddons
type SelectorModel = ArrayElement<ReturnType<typeof clusterAddonsSelector>>
type ActionParams = InferActionParams<typeof listClusterAddons>

const defaultParams: ActionParams = {
  clusterId: null,
}

const usePrefParams = createUsePrefParamsHook<ActionParams & TablePrefsParams>(
  'Cluster Addons',
  listTablePrefs,
)

export const getAddonHealthStatus = (addon) => {
  if (!addon) return { variant: 'unknown' as const, label: 'Unknown' }
  const addonStatus = getClusterAddonHealthStatus(addon)
  const label = addonStatus
  let status: BadgeVariant = 'success'

  if (addonStatus === 'Error') {
    status = 'error'
  }
  if (addonStatus === 'Installing') {
    status = 'warning'
  }
  if (addonStatus === 'Unknown') {
    status = 'unknown'
  }
  return { variant: status, label }
}

const columns = [
  { key: 'type', label: 'Add-on Name', formatFn: (type) => clusterAddonDisplayNames[type] },
  {
    key: 'healthy',
    label: 'Health Status',
    accessor: (addon) => addon,
    CellComponent: createGridStatusCell({
      dataFn: getAddonHealthStatus,
    }),
  },
  { key: 'version', label: 'Version' },
]

export default function ClusterAddonsPage({ cluster, clusterType = undefined }) {
  const classes = useStyles()
  const { history } = useReactRouter()
  const { params, getParamsUpdater } = usePrefParams(defaultParams)
  const { loading, message, reload } = useListAction(listClusterAddons, {
    params: { clusterId: cluster?.uuid },
  })
  const addons = useSelectorWithParams(clusterAddonsSelector, { clusterId: cluster?.uuid })

  const allAddons = useMemo(() => {
    const allAddons = addons.filter(
      (addon) =>
        !isUninstalling(addon) &&
        !(addon.type === ClusterAddonType.ProfileAgent || addon.type === ClusterAddonType.Metal3),
    )

    if (cluster?.etcdBackupEnabled) {
      allAddons.push(createEtcdBackupAddonObj(cluster))
    }
    return allAddons
  }, [addons, cluster])

  useEffect(() => {
    // Refetch the addons to get the latest statuses
    reload(true)
  }, [])

  const handleEditAddonsClick = useCallback(() => {
    const route =
      clusterType === ClusterTypes.Imported
        ? routes.cluster.imported.edit.path({
            id: cluster?.uuid,
            tab: IEditImportedClusterPageTabs.Addons,
          })
        : routes.cluster.managed.qbert.edit.path({
            id: cluster?.uuid,
            tab: IClusterEditPageTabs.Addons,
          })
    history.push(route)
  }, [cluster?.uuid, clusterType])

  return (
    <div className={classes.addons}>
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.ClusterAddons}
        searchTargets={['name', 'type']}
        uniqueIdentifier="type"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={allAddons}
        columns={columns}
        getParamsUpdater={getParamsUpdater}
        showBreadcrumbs={false}
        label="Cluster Add-ons"
        {...pick(listTablePrefs, params)}
      />
      <div className={classes.sideContent}>
        <div>
          <div className={classes.editAddonsButton}>
            <Button variant="secondary" icon="edit" size="medium" onClick={handleEditAddonsClick}>
              Edit Add-ons
            </Button>
          </div>
          <AddonHealthStatusesCard addons={allAddons} />
        </div>
        {/* {clusterType !== ClusterTypes.Imported && <BareMetalCard />} */}
      </div>
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  addons: {
    display: 'grid',
    gridTemplateColumns: '1fr 395px',
    gridGap: theme.spacing(4),
    paddingTop: theme.spacing(2),
  },
  sideContent: {
    display: 'grid',
    gap: theme.spacing(3),
  },
  editAddonsButton: {
    display: 'flex',
    // justifyContent: 'end',
    marginBottom: theme.spacing(2),
  },
}))
